import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import type { EventTypeView, SourcedEventView } from '@crowdex/agent-contracts/api';
import type { PullRequestCockpit } from '@crowdex/workspace-protocol/pull-request-cockpit';
import type { AcpSelectConfigOption } from '@/components/desktop-config-controls';
import { type RepositoryFile, repositoryFile } from '@/lib/desktop-shell/repository-files';
import type { AvailablePluginSkill } from '@/lib/native-agent/types';
import type { NativeAgent, NativeGitStatus, NativeWorkspaceOpener } from '@/lib/native-desktop';
import type { SidebarThread } from '@/lib/sidebar-threads';

const DEFAULT_AGENT_ID = 'auto-claude-code';

export const PREVIEW_WORKSPACE_OPENERS: readonly NativeWorkspaceOpener[] = [
  { id: 'vscode', name: 'VS Code', kind: 'ide' },
  { id: 'cursor', name: 'Cursor', kind: 'ide' },
  { id: 'zed', name: 'Zed', kind: 'ide' },
  { id: 'finder', name: 'Finder', kind: 'fileManager' },
  { id: 'terminal', name: 'Terminal', kind: 'terminal' },
  { id: 'iterm', name: 'iTerm2', kind: 'terminal' },
  { id: 'warp', name: 'Warp', kind: 'terminal' },
];

const PREVIEW_PULL_REQUEST: NonNullable<SidebarThread['pullRequest']> = {
  url: 'https://github.com/crowlingo/crowdex/pull/128',
  number: 128,
  title: 'Bring pull request state into every task surface',
  body: 'Adds durable pull request state across the desktop experience.',
  state: 'draft',
  baseBranch: 'main',
  headBranch: 'v3-electron-acp',
  updatedAt: '2026-08-14T10:00:00.000Z',
};

const MINUTE = 60_000;
/** When the preview loaded: its conversations are as old as the boards show them. */
const PREVIEW_NOW = Date.now();
const ago = (minutes: number) => PREVIEW_NOW - minutes * MINUTE;

/**
 * The conversations of the redesign's boards (D1–D4): Crowlingo's projects (Crowdex Mobile,
 * Launch, Infra & CI) and conversations, and Personal's. Their places are in
 * `preview-workspace-directory`.
 */
export const PREVIEW_THREADS: readonly SidebarThread[] = [
  {
    id: 'preview-active',
    title: 'Mobile interface inspiration',
    project: 'crowdex',
    runtime: 'cloud',
    branch: 'ccr-2368d65e',
    pullRequest: PREVIEW_PULL_REQUEST,
    loading: true,
    updatedAt: ago(1),
  },
  {
    id: 'preview-ci',
    title: 'Accélération CI',
    project: 'crowdex',
    runtime: 'cloud',
    updatedAt: ago(8),
  },
  {
    id: 'preview-swipe',
    title: 'Navigation swipe gestures',
    project: 'crowdex',
    runtime: 'cloud',
    actionRequired: true,
    updatedAt: ago(13),
  },
  {
    id: 'preview-extensions',
    title: 'Extensions cloud mobile issues',
    project: 'crowdex',
    runtime: 'cloud',
    pullRequest: { ...PREVIEW_PULL_REQUEST, number: 552, state: 'open', title: 'Cloud extensions' },
    updatedAt: ago(30),
  },
  // Its plan's lead (Work as a team): wave 1 under way, its tasks beside it in the project.
  {
    id: 'preview-team',
    title: 'Mobile navigation',
    project: 'crowdex',
    runtime: 'cloud',
    updatedAt: ago(2),
  },
  {
    id: 'preview-copy',
    title: 'Drawer copy in French',
    project: 'crowdex',
    runtime: 'cloud',
    updatedAt: ago(3),
  },
  {
    id: 'preview-landing',
    title: 'Landing de lancement',
    project: 'crowdex-site',
    runtime: 'cloud',
    loading: true,
    updatedAt: ago(4),
  },
  {
    id: 'preview-teaser',
    title: 'Teaser video',
    project: 'crowdex-site',
    runtime: 'cloud',
    updatedAt: ago(55),
  },
  {
    id: 'preview-ci-docker',
    title: 'Accélération CI · Docker',
    project: 'crowdex',
    runtime: 'cloud',
    loading: true,
    updatedAt: ago(6),
  },
  {
    id: 'preview-e2e',
    title: 'Nightly e2e flakes',
    project: 'crowdex',
    runtime: 'local',
    updatedAt: ago(600),
  },
  {
    id: 'preview-weekly',
    title: 'Weekly dependency update',
    project: 'crowdex',
    runtime: 'cloud',
    updatedAt: ago(42),
  },
  {
    id: 'preview-weekly-run-1',
    title: 'Weekly dependency update · Oct 18',
    project: 'crowdex',
    runtime: 'cloud',
    updatedAt: ago(7 * 24 * 60),
  },
  {
    id: 'preview-weekly-run-2',
    title: 'Weekly dependency update · Oct 25',
    project: 'crowdex',
    runtime: 'cloud',
    updatedAt: ago(43),
  },
  {
    id: 'preview-scroll',
    title: 'Bug affichage mobile scroll',
    project: 'crowdex',
    runtime: 'local',
    pullRequest: { ...PREVIEW_PULL_REQUEST, number: 555, state: 'merged', title: 'Mobile scroll' },
    updatedAt: ago(180),
  },
  {
    id: 'preview-release',
    title: 'Release notes 2.4',
    project: 'crowdex',
    // A worktree of its own, its pull request open and its checks failing.
    runtime: 'worktree',
    branch: 'release-notes-2-4',
    worktreeName: 'release-notes-2-4',
    pullRequest: {
      ...PREVIEW_PULL_REQUEST,
      number: 561,
      state: 'open',
      checks: 'failing',
      title: 'Release notes 2.4',
      headBranch: 'release-notes-2-4',
    },
    updatedAt: ago(26 * 60),
  },
  {
    id: 'preview-drawer-copy',
    title: 'Drawer copy in French',
    project: 'crowdex',
    runtime: 'local',
    updatedAt: ago(90),
  },
  {
    id: 'preview-hero',
    title: 'Hero de lancement',
    project: 'crowdex-site',
    runtime: 'local',
    updatedAt: ago(200),
  },
  // On the linked Crowdex Cloud, filed in the Cloud's own workspace (one this directory does
  // not hold): it shows in Personal, with its cloud glyph.
  {
    id: 'preview-cloud-triage',
    title: 'Triage support inbox',
    project: 'crowdex',
    runtime: 'cloud',
    placement: {
      workspaceId: 'cloud-ws-personal',
      projectId: null,
      parentConversationId: null,
      workflowRun: null,
    },
    updatedAt: ago(75),
  },
] as const;

/**
 * `?pinned` on a preview: the conversations it pins (D22), one outside any project and one of
 * Launch, the latest pinned first. Without it the preview pins nothing, as the boards show.
 */
export const PREVIEW_PINNED_CONVERSATIONS: readonly string[] = [
  'preview-release',
  'preview-teaser',
];

export const PREVIEW_MODEL_OPTION: AcpSelectConfigOption = {
  id: 'model',
  name: 'Model',
  category: 'model',
  type: 'select',
  currentValue: 'opus-5.5',
  options: [
    { value: 'opus-5.5', name: 'Opus 5.5', description: 'Complex work and everyday tasks' },
    { value: 'fable-5.1', name: 'Fable 5.1', description: 'The hardest problems' },
    { value: 'sonnet-5.5', name: 'Sonnet 5.5', description: 'Fast, for simpler tasks' },
  ],
};

export const PREVIEW_EFFORT_OPTION: AcpSelectConfigOption = {
  id: 'effort',
  name: 'Effort',
  category: 'thought_level',
  type: 'select',
  currentValue: 'medium',
  options: [
    { value: 'low', name: 'Low' },
    { value: 'medium', name: 'Medium' },
    { value: 'high', name: 'High' },
    { value: 'xhigh', name: 'xhigh' },
    { value: 'max', name: 'Max', description: 'Consumes usage limits faster' },
  ],
};

/** The repository files the preview's @ offers: the changed ones (D7c). */
export const PREVIEW_REPOSITORY_FILES: readonly RepositoryFile[] = [
  repositoryFile('apps/web/src/components/mobile-shell/mobile-shell.tsx', 'M'),
  repositoryFile('apps/web/src/components/mobile-shell/mobile-drawer.tsx', 'A'),
  repositoryFile('apps/web/src/components/mobile-shell/use-swipe-back.ts', 'M'),
  repositoryFile('apps/web/src/components/mobile-shell/activity-model.ts', 'M'),
];

/** The skills the preview's "/" lists (D7b). */
export const PREVIEW_SKILLS: readonly AvailablePluginSkill[] = [
  {
    name: 'design',
    description: 'Create or adapt a design: interface, dashboard, mobile UI, presentation, poster…',
    path: 'skills/design/SKILL.md',
    pluginName: 'crowdex.design',
    pluginDisplayName: 'Design',
    uri: 'skill://crowdex.design/design',
  },
  {
    name: 'dashboards',
    description: "Build or change a live dashboard of a product's numbers, from connected services",
    path: 'skills/dashboards/SKILL.md',
    pluginName: 'crowdex.dashboards',
    pluginDisplayName: 'Dashboards',
    uri: 'skill://crowdex.dashboards/dashboards',
  },
  {
    name: 'automation',
    description: "Create and edit the workspace's automations: delays, approvals, app actions",
    path: 'skills/automation/SKILL.md',
    pluginName: 'crowdex.automation',
    pluginDisplayName: 'Automations',
    uri: 'skill://crowdex.automation/automation',
  },
  {
    name: 'debug',
    description: 'Debug from runtime evidence: reproduce, test hypotheses, verify the fix',
    path: 'skills/debug/SKILL.md',
    pluginName: 'crowdex.debug',
    pluginDisplayName: 'Debug',
    uri: 'skill://crowdex.debug/debug',
  },
];

/** The permissions of the preview's conversation, as the composer's chip shows them. */
const PREVIEW_PERMISSION_OPTION: AcpSelectConfigOption = {
  id: 'permissions',
  name: 'Permissions',
  category: 'permission',
  type: 'select',
  currentValue: 'auto',
  options: [
    { value: 'ask', name: 'Ask', description: 'Asks before every edit and command' },
    { value: 'auto', name: 'Auto', description: 'Edits on its own, asks before commands' },
    { value: 'full', name: 'Full access', description: 'Acts without asking' },
  ],
};

/** The settings of the preview's conversation: its model and effort, and its permissions. */
export function previewConfigOptions(model: string, effort: string): AcpSelectConfigOption[] {
  return [
    { ...PREVIEW_MODEL_OPTION, currentValue: model },
    { ...PREVIEW_EFFORT_OPTION, currentValue: effort },
    PREVIEW_PERMISSION_OPTION,
  ];
}

/** The plan period the preview's model popover shows (D8). */
export const PREVIEW_PLAN_USAGE = {
  label: 'Plan · current session',
  ratio: 0.07,
  resets: 'Resets in 2 h 50 min',
};

const iso = (minutesAgo: number) => new Date(ago(minutesAgo)).toISOString();

/** A finished check of the preview's pull request: `seconds` long, done a few minutes ago. */
function previewCheck(name: string, seconds: number): PullRequestCockpit['checks'][number] {
  const completed = ago(3);
  return {
    id: `preview-check-${name}`,
    name,
    status: 'completed',
    conclusion: 'success',
    detailsUrl: 'https://github.com/crowlingo/crowdex/actions',
    title: null,
    summary: null,
    startedAt: new Date(completed - seconds * 1000).toISOString(),
    completedAt: new Date(completed).toISOString(),
  };
}

/** The pull request of the boards' Pull request panel (D36, mobile 33). */
export const PREVIEW_PULL_REQUEST_COCKPIT: PullRequestCockpit = {
  pullRequest: {
    ...PREVIEW_PULL_REQUEST,
    url: 'https://github.com/crowlingo/crowdex/pull/556',
    number: 556,
    title: 'feat(mobile): a drawer instead of the tab bar',
    body: "Replaces the phone's tab bar with a drawer: Activity, Library, Automations, Plugins, Connections, pinned apps and recent conversations.",
    state: 'open',
    headBranch: 'ccr-2368d65e',
  },
  author: { login: 'jonas', avatarUrl: null },
  headSha: '2a4ae5f3',
  baseSha: '20e1a84d',
  mergeable: 'mergeable',
  autoMergeEnabled: false,
  reviewDecision: 'review_required',
  additions: 120,
  deletions: 30,
  changedFiles: 4,
  commits: 3,
  commitItems: [],
  labels: ['area/web'],
  requestedReviewers: [],
  checks: [
    previewCheck('lint', 38),
    previewCheck('typecheck', 60),
    previewCheck('unit (web)', 120),
    previewCheck('unit (server)', 60),
    previewCheck('build', 180),
    {
      id: 'preview-check-e2e-mobile',
      name: 'e2e (mobile)',
      status: 'in_progress',
      conclusion: null,
      detailsUrl: 'https://github.com/crowlingo/crowdex/actions',
      title: null,
      summary: null,
      startedAt: iso(2),
      completedAt: null,
    },
  ],
  feedback: [],
  reviewThreads: [
    {
      id: 'preview-thread-1',
      path: 'apps/web/src/components/desktop-message-part.tsx',
      line: 44,
      startLine: null,
      side: 'right',
      isResolved: false,
      isOutdated: false,
      viewerCanResolve: true,
      viewerCanUnresolve: true,
      comments: [
        {
          id: 'preview-feedback-1',
          kind: 'inline',
          author: { login: 'reviewer', avatarUrl: null },
          body: 'Keep the drawer open while a swipe-back is in progress.',
          state: null,
          path: 'apps/web/src/components/desktop-message-part.tsx',
          line: 44,
          diffHunk:
            '@@ -42,4 +42,6 @@ export function DesktopMessagePart() {\n-  return <div className="tool-card" />;\n+  return <Tool defaultOpen={hasError}>\n+    <ToolHeader {...props} />\n+  </Tool>;',
          url: 'https://github.com/crowlingo/crowdex/pull/556',
          createdAt: iso(12),
          reactions: [{ emoji: '👍', count: 1 }],
          replyToId: null,
        },
      ],
    },
    {
      id: 'preview-thread-2',
      path: 'packages/ui/src/components/ai-elements/tool.tsx',
      line: 19,
      startLine: null,
      side: 'right',
      isResolved: true,
      isOutdated: false,
      viewerCanResolve: true,
      viewerCanUnresolve: true,
      comments: [
        {
          id: 'preview-feedback-2',
          kind: 'inline',
          author: { login: 'reviewer', avatarUrl: null },
          body: 'Nit: the header gap matches the other tool cards now.',
          state: null,
          path: 'packages/ui/src/components/ai-elements/tool.tsx',
          line: 19,
          url: 'https://github.com/crowlingo/crowdex/pull/556',
          createdAt: iso(10),
          reactions: [],
          replyToId: null,
        },
      ],
    },
  ],
  reactions: [],
};

export const PREVIEW_REVIEW_DIFF = `diff --git a/apps/web/src/components/desktop-message-part.tsx b/apps/web/src/components/desktop-message-part.tsx
index 39cc201..a61c29a 100644
--- a/apps/web/src/components/desktop-message-part.tsx
+++ b/apps/web/src/components/desktop-message-part.tsx
@@ -42,4 +42,6 @@ export function DesktopMessagePart() {
-  return <div className="tool-card" />;
+  return <Tool defaultOpen={hasError}>
+    <ToolHeader {...props} />
+  </Tool>;
 }
diff --git a/packages/ui/src/components/ai-elements/tool.tsx b/packages/ui/src/components/ai-elements/tool.tsx
index 8237ccd..730e4fc 100644
--- a/packages/ui/src/components/ai-elements/tool.tsx
+++ b/packages/ui/src/components/ai-elements/tool.tsx
@@ -18,3 +18,4 @@ export function ToolHeader() {
-  return <header>{title}</header>;
+  return <header className="flex items-center gap-2">{title}</header>;
 }
`;

export const PREVIEW_MESSAGES: CrowdexUIMessage[] = [
  {
    id: 'preview-user',
    role: 'user',
    parts: [{ type: 'text', text: 'Review the chat interface and make it feel production-ready.' }],
  },
  {
    id: 'preview-assistant',
    role: 'assistant',
    // What the turn changed: the transcript's card reviews it (PREVIEW_REVIEW_DIFF).
    metadata: {
      checkpoint: {
        before: '1'.repeat(40),
        after: '2'.repeat(40),
        files: [
          {
            path: 'apps/web/src/components/desktop-message-part.tsx',
            status: 'modified',
            additions: 3,
            deletions: 1,
          },
          {
            path: 'packages/ui/src/components/ai-elements/tool.tsx',
            status: 'modified',
            additions: 2,
            deletions: 2,
          },
        ],
      },
    },
    parts: [
      {
        type: 'reasoning',
        text: 'I need to inspect the shared AI Elements and compare their spacing, disclosure behavior, and status language before changing the desktop presentation.',
        state: 'done',
      },
      {
        type: 'data-plan',
        data: {
          entries: [
            {
              content: 'Audit the current layout and visual hierarchy',
              priority: 'high',
              status: 'completed',
            },
            {
              content: 'Refine the sidebar, conversation, and composer',
              priority: 'high',
              status: 'completed',
            },
            {
              content: 'Verify the result at desktop sizes',
              priority: 'medium',
              status: 'in_progress',
            },
          ],
        },
      },
      {
        type: 'dynamic-tool',
        toolCallId: 'preview-read',
        toolName: 'read_file',
        title: 'Read desktop-message-part.tsx',
        state: 'output-available',
        input: { path: 'apps/web/src/components/desktop-message-part.tsx' },
        output: { lines: 96 },
      },
      {
        type: 'dynamic-tool',
        toolCallId: 'preview-edit',
        toolName: 'apply_patch',
        title: 'Edited tool call components',
        state: 'output-available',
        input: { path: 'packages/ui/src/components/ai-elements/tool.tsx' },
        output: { changed: true },
      },
      {
        type: 'dynamic-tool',
        toolCallId: 'preview-command',
        toolName: 'exec_command',
        title: 'Run typecheck',
        state: 'input-available',
        input: { command: 'pnpm --filter @crowdex/web typecheck' },
      },
      {
        type: 'dynamic-tool',
        toolCallId: 'preview-error',
        toolName: 'exec_command',
        title: 'Run visual regression tests',
        state: 'output-error',
        input: { command: 'pnpm test:visual' },
        errorText: 'Command not found: test:visual',
      },
      {
        type: 'data-diff',
        data: {
          toolCallId: 'preview-edit',
          path: 'apps/web/src/components/desktop-message-part.tsx',
          oldText: 'return <div className="tool-card" />;',
          newText: 'return <Tool defaultOpen={hasError}>\n  <ToolHeader {...props} />\n</Tool>;',
        },
      },
      {
        type: 'data-terminal',
        data: {
          toolCallId: 'preview-command',
          terminalId: 'terminal-1',
          output: '✓ TypeScript\n✓ Biome\n✓ Production build',
          truncated: false,
          exitCode: 0,
          signal: null,
        },
      },
      {
        type: 'dynamic-tool',
        toolCallId: 'preview-bg-dev',
        toolName: 'exec_command',
        title: 'Start dev server',
        state: 'input-available',
        input: { command: 'pnpm --filter @crowdex/web dev' },
      },
      {
        type: 'data-terminal',
        data: {
          toolCallId: 'preview-bg-dev',
          terminalId: 'terminal-bg-dev',
          output:
            'Command is running in the background, output is being written to: /tmp/crowdex-dev.output',
          truncated: false,
          exitCode: null,
          signal: null,
        },
      },
      {
        type: 'text',
        text: 'I cleaned up the core chat surface and tightened the spacing, contrast, and component states. The result now feels quieter and more focused while keeping the agent controls close at hand.',
      },
      {
        type: 'data-terminal',
        data: {
          toolCallId: 'preview-bg-build',
          terminalId: 'terminal-bg-build',
          output:
            'Command is running in the background, output is being written to: /tmp/crowdex-build.output',
          truncated: false,
          exitCode: 0,
          signal: null,
        },
      },
      {
        type: 'data-usage',
        data: { used: 18420, size: 200000, cost: null },
      },
    ],
  },
];

export const PREVIEW_AGENT: NativeAgent = {
  id: DEFAULT_AGENT_ID,
  name: 'Claude Code',
  command: 'claude-agent-acp',
  args: [],
  env: {},
};

export const PREVIEW_GIT_STATUS: NativeGitStatus = {
  branch: 'v3-electron-acp',
  defaultBranch: 'main',
  remoteUrl: 'git@github.com:crowlingo/crowdex.git',
  branches: ['v3-electron-acp', 'crowdex/state', 'main', 'develop', 'task/dis-bonjour'],
  remoteBranches: ['main', 'develop', 'crowdex/state', 'release/2024.12'],
  worktrees: [
    {
      path: '/Users/jonas/Projects/crowdex',
      branch: 'v3-electron-acp',
      isCurrent: true,
      managed: false,
      ownerThreadId: null,
      sourceRoot: '/Users/jonas/Projects/crowdex',
      permanent: false,
    },
    {
      path: '/Users/jonas/Projects/crowdex-worktrees/redesign',
      branch: 'feature/redesign',
      isCurrent: false,
      managed: false,
      ownerThreadId: null,
      sourceRoot: '/Users/jonas/Projects/crowdex',
      permanent: true,
    },
  ],
  filesChanged: 19,
  fileStats: [],
  additions: 1514083,
  deletions: 310424,
  hasChanges: true,
  commitsAhead: 1,
  hasUpstream: true,
  pullRequestUrl: 'https://github.com/crowlingo/crowdex/pull/128',
  pullRequest: {
    url: 'https://github.com/crowlingo/crowdex/pull/128',
    number: 128,
    title: 'Bring pull request state into every task surface',
    body: 'Adds durable pull request state across the desktop experience.',
    state: 'draft',
    baseBranch: 'main',
    headBranch: 'v3-electron-acp',
    updatedAt: '2026-08-14T10:00:00.000Z',
  },
  pullRequestAvailability: 'available',
};

function previewEvent(
  event: Pick<SourcedEventView, 'id' | 'title' | 'type'> & Partial<SourcedEventView>,
  minutes: number,
): SourcedEventView {
  return {
    plugin: null,
    body: null,
    severity: 'info',
    fields: {},
    link: null,
    url: null,
    threadId: null,
    group: null,
    delivery: 'list',
    readAt: null,
    source: '',
    ...event,
    createdAt: new Date(ago(minutes)).toISOString(),
  };
}

const readAgo = (minutes: number) => new Date(ago(minutes)).toISOString();

/**
 * The notifications of the boards (D4, mobile 46), and the two conversations waiting on the user
 * (Needs you: a question, an approval).
 */
export const PREVIEW_NOTIFICATIONS: readonly SourcedEventView[] = [
  previewEvent(
    {
      id: 'preview-waiting-ci',
      type: 'crowdex/agent.waiting',
      title: 'Accélération CI asks you',
      body: "Tu veux que j'ouvre une PR pour voir les durées réelles ?",
      severity: 'warning',
      threadId: 'preview-ci',
      // Answered from its card (Needs you): its turn, its request and its few answers.
      fields: {
        kind: 'question',
        turn: '00000000-0000-4000-8000-000000000001',
        request: 'question-1',
        answer: 'form',
        field: 'answer',
        choices: JSON.stringify([{ value: 'yes', label: 'Oui, ouvre-la' }]),
      },
    },
    8,
  ),
  previewEvent(
    {
      id: 'preview-waiting-swipe',
      type: 'crowdex/agent.waiting',
      title: 'Navigation swipe gestures waits on you',
      body: 'pnpm --filter web test',
      severity: 'warning',
      threadId: 'preview-swipe',
      fields: {
        kind: 'approval',
        turn: '00000000-0000-4000-8000-000000000002',
        request: 'approval-1',
        detail: 'pnpm --filter web test',
      },
    },
    13,
  ),
  previewEvent(
    {
      id: 'preview-triage',
      plugin: 'com.example.automations',
      type: 'com.example.automations/run.failed',
      title: 'Triage new issues failed',
      body: 'GitHub token expired',
      severity: 'error',
      fields: { automation: 'triage', run: 'run-1', reconnect: 'GitHub' },
      link: '/',
    },
    20,
  ),
  previewEvent(
    {
      id: 'preview-weekly',
      plugin: null,
      type: 'crowdex/request.waiting',
      title: 'Weekly dependency update waits on you',
      body: 'approve the PR it prepared',
      severity: 'warning',
      threadId: 'preview-weekly',
      fields: { plugin: 'com.example.automations', kind: 'approval', request: 'preview-request' },
      group: 'request:preview-request',
    },
    42,
  ),
  previewEvent(
    {
      id: 'preview-teaser',
      plugin: 'com.example.video',
      type: 'com.example.video/export.finished',
      title: 'teaser.mp4 is exported',
      body: '24 s · 1080p',
      severity: 'success',
      fields: { download: 'https://example.com/teaser.mp4' },
      link: '/',
    },
    55,
  ),
  previewEvent(
    {
      id: 'preview-ring',
      type: 'crowdex/assistant.ring',
      title: 'Missed call from the assistant',
      body: 'about PR #556',
      readAt: readAgo(100),
      threadId: 'preview-active',
    },
    120,
  ),
  previewEvent(
    {
      id: 'preview-replied',
      type: 'crowdex/agent.replied',
      title: 'Bug affichage mobile scroll finished',
      body: 'read in the conversation',
      severity: 'success',
      threadId: 'preview-scroll',
      readAt: readAgo(150),
    },
    180,
  ),
  // Its earlier replies that day: one row with the latest (D4, "3 replies").
  ...[200, 230].map((minutes, index) =>
    previewEvent(
      {
        id: `preview-replied-earlier-${index}`,
        type: 'crowdex/agent.replied',
        title: 'Bug affichage mobile scroll finished',
        body: 'read in the conversation',
        severity: 'success',
        threadId: 'preview-scroll',
        readAt: readAgo(150),
      },
      minutes,
    ),
  ),
  previewEvent(
    {
      id: 'preview-brief',
      plugin: 'com.example.automations',
      type: 'com.example.automations/run.succeeded',
      title: 'Morning brief succeeded',
      body: 'opened in Library',
      severity: 'success',
      readAt: readAgo(24 * 60),
    },
    25 * 60,
  ),
  previewEvent(
    {
      id: 'preview-film',
      plugin: 'com.example.video',
      type: 'com.example.video/export.failed',
      title: 'launch-film.mp4 failed to export',
      body: 'out of memory',
      severity: 'error',
      readAt: readAgo(24 * 60),
    },
    30 * 60,
  ),
];

/** The preview's event types, as `GET /api/events/types` lists them: their titles, which wait. */
export const PREVIEW_EVENT_TYPES: readonly EventTypeView[] = (
  [
    ['crowdex/agent.waiting', null, 'Crowdex', 'An agent waits on you', 'native', true],
    ['crowdex/request.waiting', null, 'Crowdex', 'An app waits on you', 'native', true],
    ['crowdex/agent.replied', null, 'Crowdex', 'An agent replied', 'native', false],
    ['crowdex/assistant.ring', null, 'Crowdex', 'Your assistant calls you', 'native', false],
    [
      'com.example.automations/run.failed',
      'com.example.automations',
      'Automations',
      'An automation failed',
      'native',
      false,
    ],
    [
      'com.example.automations/run.succeeded',
      'com.example.automations',
      'Automations',
      'An automation succeeded',
      'list',
      false,
    ],
    [
      'com.example.video/export.finished',
      'com.example.video',
      'Video',
      'A video is exported',
      'toast',
      false,
    ],
    [
      'com.example.video/export.failed',
      'com.example.video',
      'Video',
      'A video failed to export',
      'toast',
      false,
    ],
  ] as const
).map(([type, plugin, source, title, delivery, waits]) => ({
  type,
  source: { plugin, title: source },
  title,
  description: null,
  fields: {},
  waits,
  defaultDelivery: delivery,
  delivery,
  sources: [''],
  mixed: false,
}));
